"use client";

import type { CardGridBlock, ICmsLink } from "@mw/shared/payload-types";

import { Image } from "@components/core/Image";
import { ImageSizes } from "@ui/lib/image";
import {
  GridCard,
  GridCardHeader,
  CardContent,
  cardContentVariants,
  type CardContentType,
} from "@ui/components/cards/GridCard";

import { Block } from "../Block";
import clsx from "clsx";
import { ScrollGrid } from "@ui/components/layout/ScrollGrid";

type SliderVariant = "three-columns" | "four-columns" | "five-columns";

export interface BlockCardGridProps {
  layout?: "grid" | "slider" | null;
  title?: string | null;
  cards?: CardGridBlock["cards"];
  inverse?: boolean;
  columnCount?: "2" | "3" | "4" | null;
  theme?: "white" | "gray" | null;
  titleSize?: "default" | "medium" | null;
  // Slider-specific props
  variant?: SliderVariant | null;
  showLegend?: boolean | null;
}

/**
 * Card Grid component that supports both static grid and slider layouts.
 * - Grid mode: Static CSS grid with variable card column spans
 * - Slider mode: Horizontal scrolling carousel with auto-scroll
 */
export function BlockCardGrid({
  layout = "grid",
  title,
  cards,
  inverse,
  columnCount = "2",
  theme,
  titleSize = "default",
  variant = "three-columns",
  showLegend = true,
}: BlockCardGridProps) {
  // Apply layout-specific theme defaults (grid: white, slider: gray)
  const effectiveTheme = theme ?? (layout === "slider" ? "gray" : "white");
  if (!cards || cards.length === 0) {
    return null;
  }

  // Slider layout - uses original BlockCardSlider behavior
  if (layout === "slider") {
    return (
      <Block name='card-slider'>
        <ScrollGrid
          items={cards ?? []}
          columns={!variant || variant === "three-columns" ? 3 : variant === "five-columns" ? 5 : 4}
          legend={{
            show: showLegend ?? true,
            getItemTitle: card => card.title,
          }}
          renderItem={card => {
            const cardLink = (card as any).link as ICmsLink | null | undefined;
            const hasLink = !!(cardLink?.reference || cardLink?.url);

            return (
              <GridCard
                colSpan='1'
                padding={card.mainContent?.imageLayout === "stretch" ? "none" : card.padding}
                theme={effectiveTheme}
                hasLink={hasLink}
              >
                <div
                  className={cardContentVariants({ contentGap: card.contentGap, height: "full", layout: "vertical" })}
                >
                  <GridCardHeader
                    description={card.description}
                    padding={card.mainContent?.imageLayout === "stretch" ? card.padding : "none"}
                    title={card.title}
                    titleSize={titleSize}
                    link={cardLink}
                  />
                  {card.mainContent && <CardContent content={card.mainContent as CardContentType} />}
                </div>
                {card.background && (
                  <Image className='absolute inset-0' resource={card.background} sizes={ImageSizes.FULL_HALF_ON_LG} />
                )}
              </GridCard>
            );
          }}
        />
      </Block>
    );
  }

  // Grid layout (default) - uses original BlockCardGrid behavior
  return (
    <Block name='card-grid'>
      <div className='container'>
        {title && <h3 className={clsx("f-heading-03 mb-52 max-w-3xl", inverse && "text-inverse")}>{title}</h3>}
        <div
          className={clsx(
            "grid-auto-rows-fr grid grid-cols-1 gap-16 rounded-xl p-4 md:grid-cols-2",
            inverse ? "bg-inverse-lighter" : effectiveTheme === "white" ? "bg-dark" : "bg-none",
            columnCount === "4" ? "lg:grid-cols-4" : columnCount === "3" ? "lg:grid-cols-3" : "lg:grid-cols-2",
          )}
        >
          {cards?.map((card, index) => {
            const cardLink = (card as any).link as ICmsLink | null | undefined;
            const hasLink = !!(cardLink?.reference || cardLink?.url);

            return (
              <GridCard
                colSpan={card.colSpan}
                key={index}
                padding={card.mainContent?.imageLayout === "stretch" ? "none" : card.padding}
                theme={inverse ? "black" : effectiveTheme}
                largePadding={(card as any).largePadding ?? "larger"}
                hasLink={hasLink}
              >
                {card.colSpan === "1" ? (
                  // Single column card layout - content below
                  <div
                    className={cardContentVariants({ contentGap: card.contentGap, height: "full", layout: "vertical" })}
                  >
                    <GridCardHeader
                      description={card.description}
                      inverse={!!inverse}
                      padding={card.mainContent?.imageLayout === "stretch" ? card.padding : "none"}
                      title={card.title}
                      titleSize={titleSize}
                      link={cardLink}
                    />
                    {card.mainContent && <CardContent content={card.mainContent as CardContentType} />}
                  </div>
                ) : card.largeLayout === "vertical" ? (
                  // Double column card layout - vertical (content below)
                  <div
                    className={cardContentVariants({ contentGap: card.contentGap, height: "full", layout: "vertical" })}
                  >
                    <GridCardHeader
                      description={card.description}
                      inverse={!!inverse}
                      padding={card.mainContent?.imageLayout === "stretch" ? card.padding : "none"}
                      title={card.title}
                      titleSize={titleSize}
                      link={cardLink}
                    />
                    {card.mainContent && <CardContent content={card.mainContent as CardContentType} />}
                  </div>
                ) : (
                  // Double column card layout - horizontal (content on right)
                  <div
                    className={cardContentVariants({
                      contentGap: card.contentGap,
                      height: "auto",
                      layout: "horizontal",
                    })}
                  >
                    <div className='flex basis-2/5 flex-col justify-between gap-32'>
                      <GridCardHeader
                        description={card.description}
                        inverse={!!inverse}
                        title={card.title}
                        titleSize={titleSize}
                        link={cardLink}
                      />
                    </div>
                    <div className='flex basis-3/5 flex-col items-center gap-32'>
                      {card.mainContent && <CardContent content={card.mainContent as CardContentType} />}
                    </div>
                  </div>
                )}
                {card.background && (
                  <Image className='absolute inset-0' resource={card.background} sizes={ImageSizes.FULL_HALF_ON_LG} />
                )}
              </GridCard>
            );
          })}
        </div>
      </div>
    </Block>
  );
}

BlockCardGrid.displayName = "BlockCardGrid";
